import { useEffect, useState } from "react";
import axios from "axios";
import { confirmSwal } from "../utils/confirm";
import { toast } from "react-toastify";

import { auth } from "../firebase";

export default function AdminEventsEditor() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingIndex, setSavingIndex] = useState(null);
    const [openIndex, setOpenIndex] = useState(null);
    const [archivedIndices, setArchivedIndices] = useState(new Set());

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const token = await auth.currentUser.getIdToken();
                const res = await axios.get("/api/public/events", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                const loadedEvents = (res.data.content || []).map(ev => ({
                    ...ev,
                    time: ev.time || [{ time: "", description: "" }],
                    contacts: ev.contacts || []
                }));
                
                // Determine which events are archived at load time
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const archived = new Set();
                
                loadedEvents.forEach((event, index) => {
                    if (event.date) {
                        const eventDate = new Date(event.date + "T00:00");
                        eventDate.setHours(0, 0, 0, 0);
                        const dayAfterEvent = new Date(eventDate);
                        dayAfterEvent.setDate(dayAfterEvent.getDate() + 1);
                        if (today >= dayAfterEvent) {
                            archived.add(index);
                        }
                    }
                });
                
                setArchivedIndices(archived);
                setEvents(loadedEvents);
            } catch (error) {
                console.error("Error fetching events:", error);
                alert("Error fetching events");
            } finally {
                setLoading(false);
            }
        };
        
        fetchEvents();
    }, []);

    const handleChange = (eventIndex, field, value) => {
        const newEvents = [...events];
        newEvents[eventIndex][field] = value;
        setEvents(newEvents);
    };

    const handleTimeChange = (eventIndex, timeIndex, field, value) => {
        const updatedEvents = [...events];
        updatedEvents[eventIndex].time[timeIndex][field] = value;
        setEvents(updatedEvents);
    };

    const addTimeEntry = (eventIndex) => {
        const updatedEvents = [...events];
        updatedEvents[eventIndex].time.push({ time: "", description: "", public: false });
        setEvents(updatedEvents);
    };

    const saveEvent = async (index) => {
        setSavingIndex(index);
        try {
            const token = await auth.currentUser.getIdToken();
            const headers = {
                Authorization: `Bearer ${token}`,
            };
            
            // Save all events at once
            await axios.put("/api/public/events", { content: events }, { headers });
            toast.success("Saved!");

        } catch {
            toast.error("Something went wrong");
        } finally {
            setSavingIndex(null);
        }
    };

    const deleteEvent = async (index) => {
        const event = events[index];
        const ok = await confirmSwal({
            title: `Delete event ${event.name}?`,
            text: "Once deleted it cannot be restored.",
            icon: "warning",
            confirmText: "Delete",
            confirmColor: "#d33",
        });
        if (!ok) return;

        const updatedEvents = events.filter((_, i) => i !== index);
        setEvents(updatedEvents);
        if (openIndex === index) setOpenIndex(null);
        
        // Update archived indices - remove the deleted index and shift down all higher indices
        const newArchivedIndices = new Set();
        archivedIndices.forEach(archivedIndex => {
            if (archivedIndex < index) {
                newArchivedIndices.add(archivedIndex);
            } else if (archivedIndex > index) {
                newArchivedIndices.add(archivedIndex - 1);
            }
            // If archivedIndex === index, don't add it (it's deleted)
        });
        setArchivedIndices(newArchivedIndices);

        try {
            const token = await auth.currentUser.getIdToken();
            await axios.put("/api/public/events", { content: updatedEvents }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            toast.info("Event deleted");
        } catch {
            toast.error("Failed to save after deletion");
        }
    };

    const toggleOpen = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const addEvent = () => {
        const newEvent = {
            name: "",
            description: "",
            date: "",
            location: "",
            address: "",
            // co_chairs: "",
            contacts: [],
            public: true,
            time: [{ time: "", description: "" }],
        };
        setEvents([newEvent, ...events]);
        
        // Update archived indices - shift all indices by 1 since we're adding at the beginning
        const newArchivedIndices = new Set();
        archivedIndices.forEach(index => newArchivedIndices.add(index + 1));
        setArchivedIndices(newArchivedIndices);
        
        setOpenIndex(0);
    };

    if (loading) return <p>Loading events...</p>;

    // Separate events into active and archived based on the snapshot taken at load time
    const activeEvents = events.filter((event, index) => !archivedIndices.has(index));
    const archivedEvents = events.filter((event, index) => archivedIndices.has(index));

    return (
        <div className="container">
            <p className="text-body-secondary">Items here are showed publicly on the <em>Events page</em>.</p>
            <p className="text-body-secondary">Events are automatically archived the day after the event date.</p>

            <button className="btn btn-admin mb-3" onClick={addEvent}>+ Add Event</button>

            <h4 className="mb-3">Active Events</h4>
            {activeEvents.length === 0 && <p className="text-muted">No active events</p>}
            
            <div className="accordion mb-5" id="eventsAccordion">
                {activeEvents.map((event, index) => {
                    const actualIndex = events.indexOf(event);
                    const displayDate = event.date
                        ? new Date(event.date + "T00:00").toLocaleDateString(undefined, {
                            weekday: "long", year: "numeric", month: "long", day: "numeric"
                        })
                        : "No Date";

                    return (
                        <div className="accordion-item" key={index}>
                            <h2 className="accordion-header" id={`heading-${actualIndex}`}>
                                <button
                                    className={`accordion-button ${openIndex === actualIndex ? "" : "collapsed"}`}
                                    type="button"
                                    onClick={() => toggleOpen(actualIndex)}
                                >
                                    <span><strong>{displayDate}</strong> {event.name || "(No Name)"}</span>
                                </button>
                            </h2>

                            <div className={`accordion-collapse collapse ${openIndex === actualIndex ? "show" : ""}`}>
                                <div className="accordion-body">
                                    <h5 className="border-bottom pb-2 mb-3">Event Info</h5>
                                    
                                    <div className="mb-2">
                                        <label className="form-label">Name</label>
                                        <input className="form-control" value={event.name} onChange={(e) => handleChange(actualIndex, "name", e.target.value)} />
                                    </div>

                                    <div className="mb-2">
                                        <label className="form-label">Description <small className="text-body-secondary">(Optional)</small></label>
                                        <textarea 
                                            className="form-control" 
                                            rows="3"
                                            placeholder="Event description..."
                                            value={event.description || ""} 
                                            onChange={(e) => handleChange(actualIndex, "description", e.target.value)} 
                                        />
                                    </div>

                                    <div className="mb-2">
                                        <label className="form-label">Date</label>
                                        <input type="date" className="form-control" value={event.date} onChange={(e) => handleChange(actualIndex, "date", e.target.value)} />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label d-block">Contacts <small className="text-body-secondary">(Optional)</small></label>
                                        {(Array.isArray(event.contacts) && event.contacts.length > 0) && (
                                            <div className="accordion mb-2" id={`contactsAccordion-${actualIndex}`}>
                                                {event.contacts.map((contact, cIndex) => (
                                                <div key={cIndex} className="accordion-item">
                                                    <h2 className="accordion-header" id={`contact-heading-${actualIndex}-${cIndex}`}>
                                                        <button
                                                            className={`accordion-button collapsed`}
                                                            type="button"
                                                            data-bs-toggle="collapse"
                                                            data-bs-target={`#contact-${actualIndex}-${cIndex}`}
                                                        >
                                                            <strong>{contact.name || `Contact ${cIndex + 1}`}</strong>
                                                        </button>
                                                    </h2>
                                                    <div
                                                        id={`contact-${actualIndex}-${cIndex}`}
                                                        className="accordion-collapse collapse"
                                                    >
                                                        <div className="accordion-body">
                                                            <div className="mb-2">
                                                                <label className="form-label">Name</label>
                                                                <input
                                                                    className="form-control"
                                                                    placeholder="Name"
                                                                    value={contact.name || ""}
                                                                    onChange={(e) => {
                                                                        const newContacts = [...event.contacts];
                                                                        newContacts[cIndex].name = e.target.value;
                                                                        handleChange(actualIndex, "contacts", newContacts);
                                                                    }}
                                                                />
                                                            </div>
                                                            <div className="mb-2">
                                                                <label className="form-label">Phone</label>
                                                                <input
                                                                    className="form-control"
                                                                    placeholder="Phone"
                                                                    value={contact.phone || ""}
                                                                    onChange={(e) => {
                                                                        const newContacts = [...event.contacts];
                                                                        newContacts[cIndex].phone = e.target.value;
                                                                        handleChange(actualIndex, "contacts", newContacts);
                                                                    }}
                                                                />
                                                            </div>
                                                            <div className="mb-2">
                                                                <label className="form-label">Email</label>
                                                                <input
                                                                    className="form-control"
                                                                    placeholder="Email"
                                                                    type="email"
                                                                    value={contact.email || ""}
                                                                    onChange={(e) => {
                                                                        const newContacts = [...event.contacts];
                                                                        newContacts[cIndex].email = e.target.value;
                                                                        handleChange(actualIndex, "contacts", newContacts);
                                                                    }}
                                                                />
                                                            </div>

                                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                                <div>
                                                                    <button
                                                                        className="btn btn-sm btn-outline-secondary me-2"
                                                                        type="button"
                                                                        disabled={cIndex === 0}
                                                                        onClick={() => {
                                                                            const newContacts = [...event.contacts];
                                                                            [newContacts[cIndex - 1], newContacts[cIndex]] = [newContacts[cIndex], newContacts[cIndex - 1]];
                                                                            handleChange(actualIndex, "contacts", newContacts);
                                                                        }}
                                                                    >
                                                                        ↑ Move Up
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm btn-outline-secondary"
                                                                        type="button"
                                                                        disabled={cIndex === event.contacts.length - 1}
                                                                        onClick={() => {
                                                                            const newContacts = [...event.contacts];
                                                                            [newContacts[cIndex], newContacts[cIndex + 1]] = [newContacts[cIndex + 1], newContacts[cIndex]];
                                                                            handleChange(actualIndex, "contacts", newContacts);
                                                                        }}
                                                                    >
                                                                        ↓ Move Down
                                                                    </button>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-danger btn-sm"
                                                                    onClick={() => {
                                                                        const newContacts = event.contacts.filter((_, i) => i !== cIndex);
                                                                        handleChange(actualIndex, "contacts", newContacts);
                                                                    }}
                                                                >
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                ))}
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            className="btn btn-custom-outline btn-sm"
                                            onClick={() => {
                                                const newContacts = Array.isArray(event.contacts) ? [...event.contacts] : [];
                                                newContacts.push({ name: "", phone: "", email: "" });
                                                handleChange(actualIndex, "contacts", newContacts);
                                                
                                                // Open the newly added contact accordion after a short delay
                                                setTimeout(() => {
                                                    const newContactIndex = newContacts.length - 1;
                                                    const collapseElement = document.getElementById(`contact-${actualIndex}-${newContactIndex}`);
                                                    if (collapseElement) {
                                                        const bsCollapse = new window.bootstrap.Collapse(collapseElement, {
                                                            toggle: true
                                                        });
                                                    }
                                                }, 100);
                                            }}
                                        >
                                            + Add Contact
                                        </button>
                                    </div>

                                    <h5 className="border-bottom pb-2 mb-3">Location Info</h5>

                                    <div className="mb-2">
                                        <label className="form-label">Location Name</label>
                                        <input 
                                            className="form-control" 
                                            placeholder="e.g., Community Center"
                                            value={event.location || ""} 
                                            onChange={(e) => handleChange(actualIndex, "location", e.target.value)} 
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Address <small className="text-body-secondary">(Optional)</small></label>
                                        <input 
                                            className="form-control" 
                                            placeholder="Street address, city, state, zip"
                                            value={event.address || ""} 
                                            onChange={(e) => handleChange(actualIndex, "address", e.target.value)} 
                                        />
                                    </div>

                                    {/* <div className="mb-2">
                                        <label className="form-label">Public?</label>
                                        <select className="form-select" value={event.public ? "yes" : "no"} onChange={(e) => handleChange(actualIndex, "public", e.target.value === "yes")}>
                                            <option value="yes">Yes</option>
                                            <option value="no">No</option>
                                        </select>
                                    </div> */}

                                    <h5 className="border-bottom pb-2 mb-3">Itinerary</h5>

                                    <div className="mb-2">
                                        {event.time?.map((entry, tIndex) => (
                                            <div key={tIndex} className="d-flex align-items-center mb-2 gap-2">
                                                <input
                                                    type="time"
                                                    className="form-control"
                                                    style={{ maxWidth: '150px' }}
                                                    value={entry.time}
                                                    onChange={(e) => {
                                                        const newTime = [...event.time];
                                                        newTime[tIndex].time = e.target.value;
                                                        handleChange(actualIndex, "time", newTime);
                                                    }}
                                                />
                                                <input
                                                    className="form-control"
                                                    placeholder="Description"
                                                    value={entry.description}
                                                    onChange={(e) => {
                                                        const newTime = [...event.time];
                                                        newTime[tIndex].description = e.target.value;
                                                        handleChange(actualIndex, "time", newTime);
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary btn-sm"
                                                    title={event.time.length === 1 ? "At least one itinerary item required" : "Remove"}
                                                    disabled={event.time.length === 1}
                                                    onClick={() => {
                                                        if (event.time.length > 1) {
                                                            const newTime = event.time.filter((_, i) => i !== tIndex);
                                                            handleChange(actualIndex, "time", newTime);
                                                        }
                                                    }}
                                                >
                                                    <i className="fa-solid fa-trash" />
                                                </button>
                                            </div>
                                        ))}

                                        <button
                                            type="button"
                                            className="btn btn-custom-outline btn-sm mt-2"
                                            onClick={() => {
                                                const newTime = [...(event.time || [])];
                                                newTime.push({ time: "", description: "" });
                                                handleChange(actualIndex, "time", newTime);
                                            }}
                                        >
                                            + Add Time Slot
                                        </button>
                                    </div>


                                    <button className="btn btn-admin me-2" onClick={() => saveEvent(actualIndex)} disabled={savingIndex === actualIndex}>
                                        {savingIndex === actualIndex ? "Saving..." : "Save Changes"}
                                    </button>
                                    <button className="btn btn-outline-secondary" onClick={() => deleteEvent(actualIndex)} disabled={savingIndex === actualIndex}>
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Archived Events Section */}
            {archivedEvents.length > 0 && (
                <>
                    <h4 className="mb-3 mt-5 text-muted">Archived Events</h4>
                    <p className="text-body-secondary small">These events are no longer shown publicly (archived the day after the event date).</p>
                    
                    <div className="accordion" id="archivedEventsAccordion">
                        {archivedEvents.map((event, index) => {
                            const actualIndex = events.indexOf(event);
                            const displayDate = event.date
                                ? new Date(event.date + "T00:00").toLocaleDateString(undefined, {
                                    weekday: "long", year: "numeric", month: "long", day: "numeric"
                                })
                                : "No Date";

                            return (
                                <div className="accordion-item" key={`archived-${index}`} style={{ opacity: 0.7 }}>
                                    <h2 className="accordion-header" id={`archived-heading-${actualIndex}`}>
                                        <button
                                            className={`accordion-button ${openIndex === actualIndex ? "" : "collapsed"}`}
                                            type="button"
                                            onClick={() => toggleOpen(actualIndex)}
                                        >
                                            <span><strong>{displayDate}</strong> {event.name || "(No Name)"}</span>
                                        </button>
                                    </h2>

                                    <div className={`accordion-collapse collapse ${openIndex === actualIndex ? "show" : ""}`}>
                                        <div className="accordion-body">
                                            
                                            <div className="mb-3">
                                                <strong>Name:</strong> {event.name || "N/A"}<br/>
                                                <strong>Date:</strong> {displayDate}<br/>
                                                {event.location && <><strong>Location:</strong> {event.location}<br/></>}
                                                {event.description && <><strong>Description:</strong> {event.description}<br/></>}
                                            </div>

                                            <button className="btn btn-outline-danger" onClick={() => deleteEvent(actualIndex)}>
                                                Delete Archived Event
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
